/**
 * ProjectFinanceDocs — sección "Documentos del proyecto" en el Finance Hub (F-31).
 *
 * Permite subir y listar documentos financieros nivel proyecto:
 *   - boq_general:     BOQ general (sin villa, sin categoría)
 *   - vendor_quote:    Presupuesto de proveedor (villa OBLIGATORIA)
 *   - expense_support: Soporte de gasto (villa OBLIGATORIA)
 *
 * Reutiliza FileUploader (Fase A) y AttachmentList.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Section } from "@/pages/Shell";
import { FileUploader } from "@/components/ui/file-uploader";
import AttachmentList from "@/components/admin/AttachmentList";
import { Plus, FolderOpen } from "@phosphor-icons/react";

const DOC_TYPES = [
  { value: "boq_general", label: "BOQ general", needs_villa: false, allows_category: false },
  { value: "vendor_quote", label: "Presupuesto de proveedor", needs_villa: true, allows_category: true },
  { value: "expense_support", label: "Soporte de gasto", needs_villa: true, allows_category: true },
];

const CATEGORY_LABELS = {
  materials: "Materiales", labor: "Mano de obra", permits: "Permisos",
  utilities: "Suministros", services: "Servicios", logistics: "Logística",
  furnishing: "Mobiliario / FF&E", other: "Otros",
};

const CATEGORIES = Object.keys(CATEGORY_LABELS);

export default function ProjectFinanceDocs() {
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [villas, setVillas] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setErr("");
    try {
      const r = await api.get("/finance/project-documents");
      setDocs(r.data || []);
    } catch (e) { setErr(formatError(e)); }
  };

  const loadVillas = async () => {
    try {
      const r = await api.get("/villas");
      setVillas(r.data || []);
    } catch { /* admin shell shows villas elsewhere — silent */ }
  };

  useEffect(() => { load(); loadVillas(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return docs;
    return docs.filter(d => d.doc_type === filter);
  }, [docs, filter]);

  const handleDelete = async (file_id) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    try {
      await api.delete(`/finance/project-documents/${file_id}`);
      await load();
    } catch (e) { setErr(formatError(e)); }
  };

  return (
    <Section
      title="Documentos del proyecto"
      subtitle="BOQ general, presupuestos de proveedor y soportes de gasto."
    >
      <div className="dv-card" data-testid="project-finance-docs">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2 text-xs">
            <FilterPill active={filter === "all"} onClick={() => setFilter("all")} testId="filter-all">
              Todos ({docs.length})
            </FilterPill>
            {DOC_TYPES.map(t => {
              const n = docs.filter(d => d.doc_type === t.value).length;
              return (
                <FilterPill
                  key={t.value}
                  active={filter === t.value}
                  onClick={() => setFilter(t.value)}
                  testId={`filter-${t.value}`}
                >
                  {t.label} ({n})
                </FilterPill>
              );
            })}
          </div>
          <button
            onClick={() => setShowUpload(true)}
            className="dv-btn dv-btn-primary text-sm"
            data-testid="finance-doc-upload-trigger"
          >
            <Plus className="w-4 h-4" weight="bold" /> Subir documento
          </button>
        </div>

        {err && <div className="text-sm text-red-700 mb-3">{err}</div>}

        {filtered.length === 0 ? (
          <div className="flex items-center gap-3 py-6 text-sm text-stone-500" data-testid="project-finance-docs-empty">
            <FolderOpen className="w-4 h-4 text-stone-400" />
            {docs.length === 0
              ? "Aún no hay documentos del proyecto."
              : "No hay documentos en esta categoría."}
          </div>
        ) : (
          <DocsTable docs={filtered} villas={villas} onDelete={handleDelete} />
        )}
      </div>

      {showUpload && (
        <UploadModal
          villas={villas}
          onClose={() => setShowUpload(false)}
          onSaved={async () => { setShowUpload(false); await load(); }}
        />
      )}
    </Section>
  );
}

function FilterPill({ children, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full border text-[11px] uppercase tracking-wider ${
        active
          ? "bg-stone-900 text-white border-stone-900"
          : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"
      }`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function DocsTable({ docs, villas, onDelete }) {
  const villaById = Object.fromEntries(villas.map(v => [v.villa_id, v.name]));
  const docTypeLabel = (t) => DOC_TYPES.find(x => x.value === t)?.label || t;
  return (
    <div className="space-y-1.5" data-testid="project-finance-docs-list">
      {docs.map(d => (
        <div
          key={d.file_id}
          className="flex items-center gap-3 text-xs border-b border-stone-100 last:border-b-0 py-2"
          data-testid={`finance-doc-row-${d.file_id}`}
        >
          <span className="px-1.5 py-0.5 rounded bg-stone-100 text-stone-700 text-[10px] uppercase tracking-wider whitespace-nowrap">
            {docTypeLabel(d.doc_type)}
          </span>
          {d.villa_id && (
            <span className="text-stone-600 truncate max-w-[160px]">{villaById[d.villa_id] || d.villa_id}</span>
          )}
          {d.category && (
            <span className="text-stone-500 italic">· {CATEGORY_LABELS[d.category] || d.category}</span>
          )}
          <div className="flex-1 min-w-0">
            <AttachmentList attachments={[d]} onDelete={onDelete} testId={`finance-doc-${d.file_id}`} />
          </div>
        </div>
      ))}
    </div>
  );
}

function UploadModal({ villas, onClose, onSaved }) {
  const [docType, setDocType] = useState("boq_general");
  const [villaId, setVillaId] = useState("");
  const [category, setCategory] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const meta = DOC_TYPES.find(x => x.value === docType);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (meta.needs_villa && !villaId) {
      setErr("Selecciona una villa para este tipo de documento.");
      return;
    }
    if (!file) {
      setErr("Adjunta un archivo.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/finance/project-documents", {
        doc_type: docType,
        villa_id: villaId || null,
        category: category || null,
        file,
      });
      await onSaved();
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="finance-doc-modal">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500 mb-1">Documentos del proyecto</div>
        <div className="font-serif-dv text-2xl mb-4">Subir documento</div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs text-stone-500">Tipo de documento</label>
            <select
              className="dv-input"
              value={docType}
              onChange={e => { setDocType(e.target.value); setVillaId(""); setCategory(""); }}
              data-testid="finance-doc-type"
            >
              {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs text-stone-500">
              Villa {meta.needs_villa && <span className="text-red-600">*</span>}
            </label>
            <select
              className="dv-input"
              value={villaId}
              onChange={e => setVillaId(e.target.value)}
              required={meta.needs_villa}
              data-testid="finance-doc-villa"
            >
              <option value="">{meta.needs_villa ? "-- Selecciona villa --" : "(Sin villa)"}</option>
              {villas.map(v => <option key={v.villa_id} value={v.villa_id}>{v.name}</option>)}
            </select>
          </div>

          {meta.allows_category && (
            <div>
              <label className="text-xs text-stone-500">Categoría (opcional)</label>
              <select
                className="dv-input"
                value={category}
                onChange={e => setCategory(e.target.value)}
                data-testid="finance-doc-category"
              >
                <option value="">(Sin categoría)</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="text-xs text-stone-500 block mb-1.5">Archivo</label>
            <FileUploader
              maxSizeMB={8}
              onSelect={setFile}
              onClear={() => setFile(null)}
              testId="finance-doc-uploader"
            />
          </div>

          {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="finance-doc-error">{err}</div>}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="dv-btn dv-btn-ghost">Cancelar</button>
            <button type="submit" disabled={busy} className="dv-btn dv-btn-primary flex-1" data-testid="finance-doc-submit">
              {busy ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
